import {
  Heading,
  VStack,
  Button,
  Box,
  Text,
  useToast,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
  IconButton,
  Spacer
} from "@chakra-ui/react";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FiTrash } from "react-icons/fi";
import { PracticeSet, PracticeSetEvent } from "../../Platform/Common/types";
import { fmtLocal } from "../../utils/dates";
import B2CReviewPracticeSet from "./B2CReviewPracticeSet";
import { Badge } from "@chakra-ui/react";

interface PracticeSetManagerProps {
  product_id: number;
}

const B2CPracticeSetManager: React.FC<PracticeSetManagerProps> = ({
  product_id,
}) => {
  const [practiceSets, setPracticeSets] = useState<PracticeSet[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const toast = useToast();
  const headerBg = useColorModeValue("gray.100", "gray.700");

  const fetchPracticeSets = useCallback(async () => {
    try {
      const b2c_apiurl =
        process.env.REACT_APP_B2C_API_URL || "https://default-url.com";

      const response = await axios.get(
        `${b2c_apiurl}/product/${product_id}/practice-sets`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
          },
        }
      );

      setPracticeSets(response.data);
    } catch (error) {
      console.error("Failed to load practice sets", error);
    }
  }, [product_id]);

  useEffect(() => {
    fetchPracticeSets();
  }, [fetchPracticeSets]);

  const handleDeletePracticeSet = async (practice_set_id: number) => {
    try {
      const b2c_apiurl =
        process.env.REACT_APP_B2C_API_URL || "https://default-url.com";

      await axios.delete(
        `${b2c_apiurl}/delete-practice-set/${practice_set_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
          },
        }
      );

      toast({
        title: "Practice Set deleted successfully",
        status: "success",
      });

      setExpandedIndex(null);
      fetchPracticeSets();
    } catch (err: any) {
      const message =
        err?.response?.data?.detail || "Error deleting practice set";

      toast({
        title: message,
        status: "error",
      });
    }
  };

  const handleAddNewPracticeSet = () => {
    setIsAddingNew(true);
    setExpandedIndex(0); // open last virtual panel
  };

const handlePracticeSetChange = (event: PracticeSetEvent) => {
  setPracticeSets((prev) => {
    switch (event.type) {
      case "CREATED": {
        setIsAddingNew(false);
        // Insert new draft at top to match backend order
        const newList = [event.practiceSet, ...prev];
        // Expand the first real accordion item
        setExpandedIndex(0);
        return newList;
      }

      case "UPDATED":
        return prev.map((ps) =>
          ps.practice_set_id === event.practiceSet.practice_set_id
            ? event.practiceSet
            : ps
        );

      case "STATUS_CHANGED":
        return prev.map((ps) =>
          ps.practice_set_id === event.id
            ? { ...ps, state: event.state }
            : ps
        );

      default:
        return prev;
    }
  });
};

  return (
    <Box as="main" mx="auto" p={2}>
      <VStack>
        <Heading size="md">Practice Sets</Heading>
      </VStack>

      <Box mt={4}>
        <Button
          mt={2}
          borderRadius="sm"
          fontSize="sm"
          fontWeight="normal"
          onClick={handleAddNewPracticeSet}
        >
          + Practice Set
        </Button>
      </Box>

    <Box mt={6}>
      <Accordion
        allowToggle
        index={expandedIndex ?? undefined}
        onChange={(index) => {
          setExpandedIndex(index as number);
          setIsAddingNew(false);
        }}
      >
                {/* Add New Panel */}
        {isAddingNew && (
          <AccordionItem>
            <AccordionButton bg={headerBg}>
              <Box flex="1" textAlign="left">
                <HStack spacing={4}>
                  <Text fontWeight="bold">
                    New Practice Set
                  </Text>
                  <Badge colorScheme="gray" variant="subtle" fontSize="0.7em">
                    DRAFT
                  </Badge>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>

            <AccordionPanel pb={4}>
              <B2CReviewPracticeSet
                product_id={product_id}
                practice_set_id={0}
                onPracticeSetChange={handlePracticeSetChange}
              />
            </AccordionPanel>
          </AccordionItem>
        )}
        {/* Existing Practice Sets */}
        {practiceSets.map((practiceSet, index) => {
          
          // State → Color mapping
          const getStateColor = (state: string) => {
            switch (state) {
              case "PUBLISHED":
                return "purple";
              case "DECOMMISSIONED":
                return "yellow";
              case "LOCKED":
                return "red";
              case "DRAFT":
              default:
                return "gray";
            }
          };

          return (
            <AccordionItem key={practiceSet.practice_set_id}>
              <AccordionButton mt={2} bg={headerBg}>
                <Box flex="1" textAlign="left">
                  <HStack spacing={4}>
                    <Text fontWeight="bold">
                      {practiceSet.title}
                    </Text>

                    <Badge
                      colorScheme={getStateColor(practiceSet.state)}
                      variant="subtle"
                      fontSize="0.7em"
                    >
                      {practiceSet.state}
                    </Badge>

                    <Text fontSize="sm" color="gray.500">
                      (ID: {practiceSet.practice_set_id})
                    </Text>

                    <Text fontSize="sm" color="gray.500">
                      Updated: {fmtLocal(practiceSet.updated_at)}
                    </Text>
                  </HStack>
                </Box>
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel pb={4}>
                {/* Delete Button */}
                <HStack justify="flex-end" mb={3}>
                  <IconButton
                    aria-label="Delete Practice Set"
                    icon={<FiTrash />}
                    size="sm"
                    colorScheme="red"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this Practice Set?"
                        )
                      ) {
                        handleDeletePracticeSet(
                          practiceSet.practice_set_id
                        );
                      }
                    }}
                  />
                </HStack>

                {/* Review Component */}
                <B2CReviewPracticeSet
                  product_id={product_id}
                  practice_set_id={practiceSet.practice_set_id}
                  onPracticeSetChange={handlePracticeSetChange}
                />
              </AccordionPanel>
            </AccordionItem>
          );
        })}


      </Accordion>
    </Box>
    </Box>
  );
};

export default B2CPracticeSetManager;